import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import type { UserCreate } from '../../client/types.gen';
import { registerUserRegisterPost } from '@/client/sdk.gen';
import './Register.css';

export default function RegisterPage() {
  const [formData, setFormData] = useState<UserCreate>({
    username: '',
    password: '',
    email_address: '',
    display_name: '',
    first_name: '',
    last_name: ''
  });
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const handleChange = (field: keyof UserCreate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!formData.username || !formData.password || !formData.email_address) {
      setError('Username, password, and email are required');
      return;
    }

    setError('');
    setIsLoading(true);
    
    try {
      const response = await registerUserRegisterPost({
        body: formData
      });

      if (response.response.status !== 201) {
        throw new Error('Registration failed');
      }

      await login(formData.username, formData.password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };
  
  return (
    <div className="login-container">
      <div className="login-box">
        <h1 className="login-title">AVANT-ELITIST</h1>
        <p className="login-subtitle">Create your account</p>
        
        <div className="login-form">
          <input
            type="text"
            placeholder="Username*"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            onKeyDown={handleKeyDown}
            className="login-input"
            disabled={isLoading}
          />
          
          <input
            type="email"
            placeholder="Email*"
            value={formData.email_address}
            onChange={(e) => handleChange('email_address', e.target.value)}
            onKeyDown={handleKeyDown}
            className="login-input"
            disabled={isLoading}
          />
          
          <input
            type="password"
            placeholder="Password*"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            onKeyDown={handleKeyDown}
            className="login-input"
            disabled={isLoading}
          />
          
          <input
            type="text"
            placeholder="First Name (optional)"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            onKeyDown={handleKeyDown}
            className="login-input"
            disabled={isLoading}
          />
          
          <input
            type="text"
            placeholder="Last Name (optional)"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            onKeyDown={handleKeyDown}
            className="login-input"
            disabled={isLoading}
          />
          
          {error && <p className="login-error">{error}</p>}
          
          <button 
            onClick={handleSubmit}
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </button>
          
          <p className="switch-text">
            Already have an account?{' '}
            <span onClick={() => navigate('/login')} className="switch-link">
              Sign in
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}